import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Sparkles, Check, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import logo from "@/assets/logo.png";

const PERKS = [
  "Full access to all 30 days, free for 7 days",
  "No card needed to start",
  "Track how you feel and see your patterns",
  "Cancel or subscribe anytime, £47.99/yr or £4.99/mo after",
];

export default function TrialStart() {
  const { user, register } = useAuth();
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user && user.role !== "admin") {
      navigate(user.onboarding_complete ? "/home" : "/onboarding");
    }
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await register(firstName, email, password);
      navigate("/onboarding");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-app flex flex-col" data-testid="trial-start-page">
      <div className="max-w-md w-full mx-auto px-6 py-10 flex-1 flex flex-col justify-center">
        <Link to="/" className="mb-8 flex justify-center">
          <img src={logo} alt="Meno Flames and Finesse" className="h-24 w-auto" />
        </Link>

        <div className="bg-brand rounded-3xl p-6 text-white mb-5">
          <div className="inline-flex items-center gap-2 text-xs font-semibold tracking-wide" style={{ color: "var(--amber)" }}>
            <Sparkles size={14} /> 7 DAYS FREE
          </div>
          <h1 className="font-serif text-2xl font-bold mt-2">Start your free trial</h1>
          <p className="text-white/75 text-sm mt-1.5">Full access for 7 days. No card, no commitment.</p>
          <div className="mt-4 space-y-2">
            {PERKS.map((p, i) => (
              <div key={i} className="flex items-start gap-2.5">
                <div className="w-5 h-5 rounded-full bg-white/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check size={12} strokeWidth={3} style={{ color: "var(--amber)" }} />
                </div>
                <span className="text-white/85 text-sm">{p}</span>
              </div>
            ))}
          </div>
        </div>

        <form onSubmit={submit} className="space-y-3" data-testid="trial-form">
          <input
            data-testid="trial-firstname"
            type="text" required placeholder="First name"
            value={firstName} onChange={(e) => setFirstName(e.target.value)}
            className="w-full px-4 py-3.5 rounded-xl border border-subtle bg-white text-ink focus:outline-none focus:border-[var(--brand)]"
          />
          <input
            data-testid="trial-email"
            type="email" required placeholder="Email address"
            value={email} onChange={(e) => setEmail(e.target.value)}
            className="w-full px-4 py-3.5 rounded-xl border border-subtle bg-white text-ink focus:outline-none focus:border-[var(--brand)]"
          />
          <input
            data-testid="trial-password"
            type="password" required minLength={6} placeholder="Create a password (min 6 characters)"
            value={password} onChange={(e) => setPassword(e.target.value)}
            className="w-full px-4 py-3.5 rounded-xl border border-subtle bg-white text-ink focus:outline-none focus:border-[var(--brand)]"
          />
          {error && <p className="text-sm text-red-600" data-testid="trial-error">{error}</p>}
          <button
            data-testid="trial-submit"
            type="submit" disabled={busy}
            className="w-full bg-brand hover:bg-[var(--brand-hover)] text-white font-semibold py-4 rounded-full flex items-center justify-center gap-2 disabled:opacity-60 transition-colors"
          >
            <Sparkles size={16} /> {busy ? "Setting up your trial…" : "Start my 7-day free trial"}
          </button>
        </form>

        <div className="flex items-center justify-center gap-2 mt-4 text-xs text-ink-muted">
          <ShieldCheck size={14} className="text-brand" /> No card required · Cancel anytime
        </div>
        <p className="text-center text-sm text-ink-muted mt-6">
          Already have an account?{" "}
          <Link to="/login" className="text-brand font-semibold" data-testid="to-login-link">Log in</Link>
        </p>
      </div>
    </div>
  );
}
